import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, PRACTICE_TZ } from "../lib/api";
import { Leaf, ArrowLeft, ArrowRight, Clock, Check, Repeat, AlertTriangle } from "lucide-react";

const STEPS = ["Service", "Pick a time", "Your details", "Sign waiver", "Confirmed"];

export default function BookingFlow() {
  const { serviceId } = useParams();
  const nav = useNavigate();

  const [step, setStep] = useState(0);
  const [service, setService] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null); // { date, slot:{start,end}, practitioner }
  const [hold, setHold] = useState(null);
  const [holdMsLeft, setHoldMsLeft] = useState(null);
  const [waiver, setWaiver] = useState(null);

  // Recurring series state
  const [weeks, setWeeks] = useState(1); // 1..4
  const [seriesStarts, setSeriesStarts] = useState(null); // array of ISO when weeks>1
  const [seriesPreview, setSeriesPreview] = useState(null); // result of /recurring-preview
  const [previewLoading, setPreviewLoading] = useState(false);

  const [form, setForm] = useState({
    first_name: "",
    last_initial: "",
    email: "",
    phone: "",
    sms_opt_in: false,
    preferred_channel: "email",
  });
  const [signatureText, setSignatureText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(null);

  // Load service + waiver
  useEffect(() => {
    api.get("/services").then(({ data }) => {
      const s = data.find((x) => x.id === serviceId);
      if (!s) { setError("Service not found"); return; }
      if (s.walk_in_only) { setError("Walk-in only — drop in during open hours."); return; }
      setService(s);
    });
    api.get("/waivers/current").then(({ data }) => setWaiver(data)).catch(() => {});
  }, [serviceId]);

  // Load availability on step 1 — extend to 35 days so users can plan a 4-week series
  useEffect(() => {
    if (step !== 1 || !service) return;
    const today = new Date();
    const from = today.toISOString().slice(0, 10);
    const to = new Date(today.getTime() + 35 * 86400000).toISOString().slice(0, 10);
    api.get(`/availability`, { params: { service_id: service.id, date_from: from, date_to: to } })
      .then(({ data }) => setAvailability(data))
      .catch((e) => setError(formatApiError(e)));
  }, [step, service]);

  // Hold timer (single-week flow only)
  useEffect(() => {
    if (!hold) return;
    const expires = new Date(hold.expires_at).getTime();
    const tick = () => {
      const left = expires - Date.now();
      setHoldMsLeft(left);
      if (left <= 0) {
        setHold(null);
        setError("Your 10-minute hold expired. Pick another time.");
        setStep(1);
      }
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [hold]);

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  // ---- slot pick ----
  const pickSlot = async (slot, practitioner, date) => {
    setError("");
    setSelectedSlot({ slot, practitioner, date });
    if (weeks === 1) {
      // Existing single-slot hold flow
      try {
        const { data } = await api.post("/bookings/hold", {
          service_id: service.id,
          start: slot.start,
        });
        setHold(data);
        setSeriesStarts(null);
        setSeriesPreview(null);
        setStep(2);
      } catch (e) {
        setError(formatApiError(e));
      }
    } else {
      // Recurring series — run preview
      setPreviewLoading(true);
      try {
        const { data } = await api.post("/bookings/recurring-preview", {
          service_id: service.id,
          start: slot.start,
          weeks,
        });
        setSeriesPreview(data);
        if (data.all_clear) {
          setSeriesStarts(data.weeks.map((w) => w.requested_start));
        } else {
          setSeriesStarts(null);
        }
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  // ---- choose alternative slot for a conflicting week ----
  const pickAlternative = (weekIdx, altStart) => {
    if (!seriesPreview) return;
    const newWeeks = seriesPreview.weeks.map((w, i) => {
      if (i !== weekIdx) return w;
      return { ...w, requested_start: altStart, status: "available", alternatives: [] };
    });
    const allClear = newWeeks.every((w) => w.status === "available");
    setSeriesPreview({ ...seriesPreview, weeks: newWeeks, all_clear: allClear });
    if (allClear) {
      setSeriesStarts(newWeeks.map((w) => w.requested_start));
    }
  };

  const proceedFromSeries = () => {
    if (!seriesStarts) return;
    setStep(2);
  };

  // ---- submit (single OR series) ----
  const submitBooking = async () => {
    setError("");
    setSubmitting(true);
    try {
      if (seriesStarts && seriesStarts.length > 1) {
        const { data } = await api.post("/bookings/recurring-confirm", {
          service_id: service.id,
          starts: seriesStarts,
          client: form,
          waiver_signature_text: signatureText,
        });
        setConfirmed({ kind: "series", ...data });
      } else {
        const { data } = await api.post("/bookings/confirm", {
          hold_id: hold.hold_id,
          client: form,
          waiver_signature_text: signatureText,
        });
        setConfirmed({ kind: "single", ...data });
      }
      setStep(4);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const validIntake = form.first_name && form.last_initial && form.email && form.phone;
  const validSignature = signatureText.trim().length >= 3;
  const isSeries = !!(seriesStarts && seriesStarts.length > 1);

  // ----- UI helpers -----
  const stepDot = (idx) => (
    <span
      className={`w-2 h-2 rounded-pill ${idx <= step ? "bg-forest" : "bg-hairline"}`}
      aria-hidden
    />
  );

  if (error && !service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-appbg p-8 text-center">
        <div data-testid="booking-error">
          <p className="font-display text-2xl text-charcoal mb-2">{error}</p>
          <Link to="/" className="text-sage hover:text-forest text-sm font-ui">← Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <header className="border-b border-hairline bg-white">
        <div className="max-w-[820px] mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="booking-logo">
            <span className="w-8 h-8 rounded-pill bg-cream flex items-center justify-center">
              <Leaf className="w-4 h-4 text-forest" strokeWidth={1.5} />
            </span>
            <span className="font-display text-xl text-charcoal">The Root Forum</span>
          </Link>
          {hold && holdMsLeft != null && holdMsLeft > 0 && step < 4 && (
            <div className="flex items-center gap-2 text-sm text-status-warning bg-status-warning-bg px-3 py-1.5 rounded-pill font-ui" data-testid="hold-timer">
              <Clock className="w-3.5 h-3.5" strokeWidth={2} />
              {Math.floor(holdMsLeft / 60000)}:{String(Math.floor((holdMsLeft % 60000) / 1000)).padStart(2, "0")} hold
            </div>
          )}
        </div>
        <div className="max-w-[820px] mx-auto px-6 lg:px-8 pb-4 flex items-center gap-2">
          {STEPS.map((_, i) => <React.Fragment key={i}>{stepDot(i)}{i < STEPS.length - 1 && <div className="h-px bg-hairline flex-1" />}</React.Fragment>)}
        </div>
      </header>

      <main className="max-w-[820px] mx-auto px-6 lg:px-8 py-10">
        {error && <div className="mb-4 px-4 py-3 bg-status-danger-bg text-status-danger text-sm rounded-md font-ui" data-testid="error-banner">{error}</div>}

        {/* STEP 0 — review service */}
        {step === 0 && service && (
          <section className="animate-fade-in">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-sage mb-3">— Step 1 of 4 · Service</p>
            <h1 className="font-display text-4xl text-charcoal mb-3">{service.name}</h1>
            <p className="text-base text-slate font-ui leading-relaxed">{service.description}</p>
            <div className="mt-8 bg-white border border-hairline rounded-lg p-6 shadow-warm">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">Duration</p>
                <p className="font-ui text-charcoal mt-1">{service.duration_minutes} minutes</p>
              </div>
            </div>
            <div className="flex justify-between mt-8">
              <Link to="/" className="text-slate hover:text-forest text-sm font-ui inline-flex items-center gap-1" data-testid="back-home"><ArrowLeft className="w-4 h-4" />Back</Link>
              <button onClick={goNext} className="px-6 py-3 bg-forest hover:bg-charcoal text-white rounded-md text-sm font-medium font-ui transition-colors" data-testid="continue-to-time">Continue<ArrowRight className="w-4 h-4 inline ml-2" /></button>
            </div>
          </section>
        )}

        {/* STEP 1 — pick a time */}
        {step === 1 && availability && (
          <section className="animate-fade-in">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-sage mb-3">— Step 2 of 4 · Pick a time</p>
            <h1 className="font-display text-4xl text-charcoal mb-2">When works for you?</h1>
            <p className="text-base text-slate font-ui mb-6">Each day shows who you'll be with.</p>

            {/* Recurrence picker */}
            <div className="mb-6 bg-white border border-hairline rounded-lg p-4 shadow-warm" data-testid="recurrence-picker">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-pill bg-cream flex items-center justify-center shrink-0">
                  <Repeat className="w-4 h-4 text-forest" strokeWidth={1.5} />
                </span>
                <div className="flex-1">
                  <p className="font-ui text-sm font-medium text-charcoal">Book this visit weekly?</p>
                  <p className="text-xs font-ui text-slate mt-0.5">Great for packages — reserve the same day &amp; time for up to 4 weeks.</p>
                  <div className="mt-3 inline-flex bg-cream border border-hairline rounded-pill p-1">
                    {[1, 2, 3, 4].map((n) => (
                      <button
                        key={n}
                        onClick={() => { setWeeks(n); setSeriesPreview(null); setSeriesStarts(null); }}
                        className={`px-4 py-1.5 text-xs font-ui rounded-pill transition-colors ${weeks === n ? "bg-forest text-white" : "text-charcoal hover:text-forest"}`}
                        data-testid={`weeks-${n}`}
                      >
                        {n === 1 ? "Just once" : `${n} weeks`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Series preview panel (shown after picking a time when weeks > 1) */}
            {seriesPreview && (
              <div className="mb-6 bg-white border-2 border-forest rounded-lg p-5 shadow-warm" data-testid="series-preview">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-lg text-charcoal">Your {weeks}-week series</h3>
                  <button onClick={() => { setSeriesPreview(null); setSeriesStarts(null); setSelectedSlot(null); }} className="text-xs font-ui text-sage hover:text-forest" data-testid="series-cancel">Change time</button>
                </div>
                <ul className="space-y-3">
                  {seriesPreview.weeks.map((w, i) => (
                    <li key={i} className="border border-hairline rounded-md p-3" data-testid={`series-week-${i + 1}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-ui text-sm text-charcoal">
                            <span className="font-medium">Week {w.week_number}</span> · {new Date(`${w.date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: PRACTICE_TZ })}
                          </p>
                          <p className="text-xs font-ui text-slate mt-0.5">
                            {new Date(w.requested_start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: PRACTICE_TZ })}
                            {w.practitioner ? ` · ${w.practitioner.display_name}` : ""}
                          </p>
                        </div>
                        {w.status === "available" && (
                          <span className="px-2.5 py-1 bg-status-success-bg text-status-success text-xs font-ui rounded-pill inline-flex items-center gap-1" data-testid={`status-available-${i + 1}`}>
                            <Check className="w-3 h-3" strokeWidth={2} /> Available
                          </span>
                        )}
                        {w.status === "conflict" && (
                          <span className="px-2.5 py-1 bg-status-warning-bg text-status-warning text-xs font-ui rounded-pill inline-flex items-center gap-1" data-testid={`status-conflict-${i + 1}`}>
                            <AlertTriangle className="w-3 h-3" strokeWidth={2} /> Conflict
                          </span>
                        )}
                        {w.status === "closed" && (
                          <span className="px-2.5 py-1 bg-status-danger-bg text-status-danger text-xs font-ui rounded-pill" data-testid={`status-closed-${i + 1}`}>Closed</span>
                        )}
                      </div>
                      {w.status === "conflict" && w.alternatives.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-hairline">
                          <p className="text-xs font-ui text-slate mb-2">Pick another time this day:</p>
                          <div className="flex flex-wrap gap-2">
                            {w.alternatives.map((alt) => (
                              <button
                                key={alt.start}
                                onClick={() => pickAlternative(i, alt.start)}
                                className="px-3 py-1.5 border border-hairline rounded-md text-xs font-ui text-charcoal hover:border-forest hover:bg-cream transition-colors"
                                data-testid={`alt-${i + 1}-${alt.start}`}
                              >
                                {new Date(alt.start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: PRACTICE_TZ })}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {w.status === "closed" && (
                        <p className="mt-2 text-xs font-ui text-slate">This day isn't open for bookings. Try a different start day or a shorter series.</p>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 flex items-center justify-between">
                  <p className="text-xs font-ui text-slate">
                    {seriesPreview.all_clear
                      ? `All ${seriesPreview.weeks.length} visits are available — ready to continue.`
                      : "Resolve the conflicts above to continue."}
                  </p>
                  <button
                    onClick={proceedFromSeries}
                    disabled={!seriesPreview.all_clear}
                    className="px-5 py-2.5 bg-forest hover:bg-charcoal disabled:opacity-40 text-white rounded-md text-sm font-medium font-ui transition-colors inline-flex items-center gap-2"
                    data-testid="series-continue"
                  >
                    Continue<ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {availability.days.filter((d) => d.slots.length > 0 && d.practitioner).map((d) => (
                <div key={d.date} className="bg-white border border-hairline rounded-lg shadow-warm overflow-hidden" data-testid={`day-${d.date}`}>
                  <div className="px-5 py-4 border-b border-hairline flex items-center gap-3">
                    {d.practitioner.photo_url ? (
                      <img src={d.practitioner.photo_url} alt="" className="w-10 h-10 rounded-pill object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-pill bg-cream flex items-center justify-center text-forest font-medium">{d.practitioner.display_name?.[0]}</div>
                    )}
                    <div>
                      <p className="font-ui font-medium text-charcoal">
                        {new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: PRACTICE_TZ })}
                      </p>
                      <p className="text-xs font-ui text-slate">With <span className="text-forest font-medium">{d.practitioner.display_name}</span></p>
                    </div>
                  </div>
                  <div className="px-5 py-4 flex flex-wrap gap-2">
                    {d.slots.map((slot) => (
                      <button
                        key={slot.start}
                        onClick={() => pickSlot(slot, d.practitioner, d.date)}
                        disabled={previewLoading}
                        className="px-4 py-2 border border-hairline rounded-md text-sm font-ui text-charcoal hover:border-forest hover:bg-cream transition-colors duration-150 disabled:opacity-40"
                        data-testid={`slot-${slot.start}`}
                      >
                        {new Date(slot.start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: PRACTICE_TZ })}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {availability.days.every((d) => d.slots.length === 0) && (
                <p className="text-slate font-ui text-sm">No availability in the next 35 days. Please check back soon.</p>
              )}
            </div>
            <div className="flex justify-between mt-8">
              <button onClick={goBack} className="text-slate hover:text-forest text-sm font-ui inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" />Back</button>
            </div>
          </section>
        )}

        {/* STEP 2 — intake */}
        {step === 2 && (hold || isSeries) && (
          <section className="animate-fade-in">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-sage mb-3">— Step 3 of 4 · Your details</p>
            <h1 className="font-display text-4xl text-charcoal mb-2">A few details.</h1>
            <p className="text-base text-slate font-ui mb-2">We only collect what we need to schedule and care for you.</p>
            {isSeries ? (
              <div className="bg-white border border-hairline rounded-md px-4 py-3 mb-6 text-sm font-ui text-charcoal" data-testid="series-summary">
                <p className="font-medium mb-2">{service.name} · {seriesStarts.length}-visit series</p>
                <ul className="space-y-1 text-xs text-slate">
                  {seriesStarts.map((s, i) => (
                    <li key={s} data-testid={`series-summary-${i + 1}`}>
                      Week {i + 1}: {new Date(s).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: PRACTICE_TZ, timeZoneName: "short" })}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="bg-white border border-hairline rounded-md px-4 py-3 mb-6 text-sm font-ui text-charcoal">
                <strong>{hold.service_name}</strong> with <strong>{hold.practitioner_name}</strong> on {new Date(hold.start).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: PRACTICE_TZ, timeZoneName: "short" })}.
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="First name" required testid="first-name">
                <input type="text" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} data-testid="input-first-name" />
              </Field>
              <Field label="Last initial" required testid="last-initial">
                <input type="text" maxLength={2} value={form.last_initial} onChange={(e) => setForm({ ...form, last_initial: e.target.value })} className={inputCls} data-testid="input-last-initial" />
              </Field>
              <Field label="Email" required className="col-span-2" testid="email">
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="input-email" />
              </Field>
              <Field label="Phone" required className="col-span-2" testid="phone">
                <input type="tel" placeholder="+15551234567" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="input-phone" />
              </Field>
              <label className="col-span-2 flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.sms_opt_in} onChange={(e) => setForm({ ...form, sms_opt_in: e.target.checked })} className="mt-1 accent-sage" data-testid="input-sms-opt-in" />
                <span className="text-sm font-ui text-slate">Send me an SMS reminder 24 hours before. Reply STOP to opt out at any time.</span>
              </label>
            </div>
            <div className="flex justify-between mt-8">
              <button onClick={goBack} className="text-slate hover:text-forest text-sm font-ui inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" />Back</button>
              <button onClick={goNext} disabled={!validIntake} className="px-6 py-3 bg-forest hover:bg-charcoal disabled:opacity-40 text-white rounded-md text-sm font-medium font-ui transition-colors" data-testid="continue-to-waiver">Continue<ArrowRight className="w-4 h-4 inline ml-2" /></button>
            </div>
          </section>
        )}

        {/* STEP 3 — waiver */}
        {step === 3 && (hold || isSeries) && waiver && (
          <section className="animate-fade-in">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-sage mb-3">— Step 4 of 4 · Waiver</p>
            <h1 className="font-display text-4xl text-charcoal mb-6">{waiver.title}</h1>
            <article className="bg-white border border-hairline rounded-lg p-6 shadow-warm prose prose-sm max-w-none font-ui text-charcoal whitespace-pre-wrap">
              {waiver.body_markdown}
            </article>
            <div className="mt-6">
              <Field label="Type your full name to sign" required testid="signature">
                <input
                  type="text"
                  value={signatureText}
                  onChange={(e) => setSignatureText(e.target.value)}
                  className={inputCls}
                  placeholder={`${form.first_name} ${form.last_initial}.`}
                  data-testid="input-signature"
                />
              </Field>
              <p className="text-xs font-ui text-slate mt-2">By typing your name, you agree to the waiver above. Timestamped and stored for the practice's records.</p>
            </div>
            <div className="flex justify-between mt-8">
              <button onClick={goBack} className="text-slate hover:text-forest text-sm font-ui inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" />Back</button>
              <button onClick={submitBooking} disabled={!validSignature || submitting} className="px-6 py-3 bg-forest hover:bg-charcoal disabled:opacity-40 text-white rounded-md text-sm font-medium font-ui transition-colors" data-testid="confirm-booking-button">
                {submitting ? "Confirming…" : "Confirm booking"}
              </button>
            </div>
          </section>
        )}

        {/* STEP 4 — confirmation */}
        {step === 4 && confirmed && confirmed.kind === "single" && (
          <section className="text-center max-w-xl mx-auto animate-fade-in" data-testid="confirmation-screen">
            <div className="w-16 h-16 mx-auto rounded-pill bg-status-success-bg flex items-center justify-center mb-6">
              <Check className="w-8 h-8 text-status-success" strokeWidth={1.5} />
            </div>
            <h1 className="font-display text-4xl text-charcoal mb-3">You're booked.</h1>
            <p className="text-base text-slate font-ui mb-8">
              We'll see you on {new Date(confirmed.scheduled_start).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: PRACTICE_TZ, timeZoneName: "short" })} with <strong>{confirmed.practitioner.display_name}</strong>.
            </p>
            <div className="bg-white border border-hairline rounded-lg p-6 shadow-warm text-left">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">Service</p>
                  <p className="font-ui text-charcoal mt-1">{confirmed.service.name}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">Practitioner</p>
                  <p className="font-ui text-charcoal mt-1">{confirmed.practitioner.display_name}</p>
                </div>
                {confirmed.uses_package && (
                  <div className="col-span-2 mt-2 bg-status-success-bg text-status-success text-sm font-ui rounded-md px-3 py-2">
                    Covered by a package on file. No payment needed today.
                  </div>
                )}
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate mt-6">Reference</p>
              <p className="font-mono text-xs text-slate mt-1 break-all">{confirmed.appointment_id}</p>
            </div>
            <Link to="/" className="inline-block mt-8 text-sage hover:text-forest text-sm font-ui">← Return to home</Link>
          </section>
        )}

        {/* STEP 4 — series confirmation */}
        {step === 4 && confirmed && confirmed.kind === "series" && (
          <section className="text-center max-w-xl mx-auto animate-fade-in" data-testid="series-confirmation-screen">
            <div className="w-16 h-16 mx-auto rounded-pill bg-status-success-bg flex items-center justify-center mb-6">
              <Check className="w-8 h-8 text-status-success" strokeWidth={1.5} />
            </div>
            <h1 className="font-display text-4xl text-charcoal mb-3">You're booked for {confirmed.appointments.length} visits.</h1>
            <p className="text-base text-slate font-ui mb-8">
              Here is your full schedule for <strong>{confirmed.service.name}</strong>:
            </p>
            <div className="bg-white border border-hairline rounded-lg p-6 shadow-warm text-left">
              <ul className="space-y-3">
                {confirmed.appointments.map((a, i) => (
                  <li key={a.appointment_id} className="flex items-center justify-between gap-3 pb-3 border-b border-hairline last:border-0 last:pb-0" data-testid={`confirmed-appt-${i + 1}`}>
                    <div>
                      <p className="font-ui text-sm text-charcoal">
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate mr-2">Week {i + 1}</span>
                        {new Date(a.scheduled_start).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: PRACTICE_TZ, timeZoneName: "short" })}
                      </p>
                      <p className="text-xs font-ui text-slate mt-0.5">With {a.practitioner.display_name}</p>
                    </div>
                    {a.uses_package && (
                      <span className="px-2 py-1 bg-status-success-bg text-status-success text-[10px] font-ui rounded-pill">Package</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate mt-6">Series reference</p>
              <p className="font-mono text-xs text-slate mt-1 break-all">{confirmed.series_id}</p>
            </div>
            <Link to="/" className="inline-block mt-8 text-sage hover:text-forest text-sm font-ui">← Return to home</Link>
          </section>
        )}
      </main>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal placeholder:text-slate focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30 transition-colors";

function Field({ label, required, children, className = "", testid }) {
  return (
    <label className={`block ${className}`} data-testid={`field-${testid}`}>
      <span className="block text-sm font-ui text-charcoal mb-1.5 flex items-center gap-1.5">
        {label}
        {required && <span className="text-sage text-xs" aria-label="required">•</span>}
      </span>
      {children}
    </label>
  );
}
